// How to play, from the Help button.
import { BREACH, DEFAULT_CONFIG, ENEMY_TYPES, NUCLEAR_SHIP_TYPES, NUCLEAR_TYPES, PRICE, REWARD, SHIP_TYPES, TILE, TOWERS } from '@thrash/sim';
import { openCredits } from './credits.ts';

const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;');

function towerGuide(): string {
  return TOWERS.map(id => {
    const b = DEFAULT_CONFIG.buildings[id]!;
    const targets = (b.targets ?? ['infantry', 'vehicle']).join(', ');
    return `<li><strong>${esc(b.name)}</strong> $${PRICE[id]}${b.real ? ` <span class="muted">${esc(b.real)}</span>` : ''} · shoots ${esc(targets)}</li>`;
  }).join('');
}

function enemyGuide(): string {
  const road = ENEMY_TYPES.map(id => {
    const u = DEFAULT_CONFIG.units[id]!;
    return `<li><strong>${esc(u.name)}</strong>${u.real ? ` <span class="muted">${esc(u.real)}</span>` : ''} · pays $${REWARD[id]} · costs ${BREACH[id]} if it gets through</li>`;
  });
  const nuclear = NUCLEAR_TYPES.map(id => {
    const u = DEFAULT_CONFIG.units[id]!;
    return `<li><strong>${esc(u.name)}</strong>${u.real ? ` <span class="muted">${esc(u.real)}</span>` : ''} · DEFCON 1 only · pays $${REWARD[id]} · costs ${BREACH[id]} if it gets through, and levels ${Math.round((u.warhead?.radius ?? 0) / TILE)} tiles round where it lands</li>`;
  });
  const launches = (id: string): string => id === 'destroyer' ? 'Kalibr cruise missiles' : id === 'submarine' ? 'nuclear ICBMs (DEFCON 1 only)' : 'Shaheds';
  const ships = [...SHIP_TYPES, ...NUCLEAR_SHIP_TYPES].map(id => {
    const u = DEFAULT_CONFIG.units[id]!;
    return `<li><strong>${esc(u.name)}</strong>${u.real ? ` <span class="muted">${esc(u.real)}</span>` : ''} · offshore, out of reach · launches ${launches(id)} at the Command Center</li>`;
  });
  return [...road, ...nuclear, ...ships].join('');
}

export function openHelp(): void {
  if (document.getElementById('help')) return;
  const wrap = document.createElement('div');
  wrap.id = 'help';
  wrap.className = 'dialog';
  wrap.innerHTML = `<div class="box" role="dialog" aria-modal="true" aria-labelledby="help-title">
    <h2 id="help-title">How to play</h2>
    <p>The enemy comes down the road, wave after wave, and whatever reaches the end wears the Command Center
      down. Put towers beside the road to stop them. Each kill pays cash; so does every wave you hold. Anything that
      flies (drones, helicopters, cruise missiles) ignores the road: it comes in from any side of the map, over the
      sea too, and heads straight for the Command Center, shooting what it passes, so air defence has to cover every
      approach. At DEFCON 1 the enemy goes nuclear from the twentieth wave: a submarine offshore fires ICBMs and the
      glide vehicles carry nuclear warheads, and only THAAD can reach them.</p>
    <h3>Building</h3>
    <p>The shop along the bottom shows one group of towers at a time — guns, mines, artillery, air defence and so on —
      with the groups as tabs along its top (Z and C step through them). Pick a tower, or press its number, then click the
      ground beside the road. The ring shows how
      far it shoots. Mines go <em>on</em> the road. Click a tower to see what it does, change what it shoots first,
      upgrade it (three levels: more damage, more reach, more hit points) or sell it for 70 percent back.</p>
    <h3>Waves</h3>
    <p>Nothing comes until you press <strong>Start</strong> (Space), unless <strong>Auto</strong> (A) is on, which sends
      the next wave a few seconds after the last is held. Call the next wave while one is still on and you are paid
      extra for your nerve. The wave chip top left shows the round. Past wave twenty the enemy comes bigger and
      tougher every wave, for the record. Each tower shoots only what it is for: machine guns for men, anti-tank
      posts for armour, air defence for what flies, interceptors for missiles.</p>
    <h3>The Sentry Turret</h3>
    <p>The first gun you buy, and one you keep paying into. Each upgrade changes how it looks and what it fires:
      <strong>Incendiary Rounds</strong> set whatever they hit burning, <strong>Twin Guns</strong> put a second barrel
      on it, <strong>Explosive Rounds</strong> burst among the men beside the one hit, and the <strong>Rotary
      Cannon</strong> is a six-barrel gatling with a radar that reaches further and shoots down drones and helicopters
      too. Air defence reaches a long way, eleven tiles and more, and costs to match. The <strong>Leonidas</strong>
      microwave fries every drone within twelve tiles in one pulse, a swarm at a time; the <strong>Phalanx</strong> is a
      short-range gatling of last resort for the missiles and drones that get close to the Command Center. Watch for
      <strong>Lancets</strong>: loitering drones that hunt the tanks, helicopters and fighters you call in.</p>
    <h3>Towers last</h3>
    <p>The enemy shoots at your towers as it goes by, but a tower you place never breaks and never runs out of
      ammunition, and a mine re-arms a few seconds after it goes off. That is why they cost what they do: choose
      where they go. It is the Command Center the enemy is after. Car bombs race up the road flat out and go off on
      it, and kamikaze fighters dive straight into it from any side.</p>
    <h3>Support</h3>
    <p>The row under the cash is what you can call in, for money, on a tile you pick. <strong>Artillery</strong> (Q,
      $300) drops shells there three seconds after the call. The rest send a unit that hunts round that tile for the
      rest of the wave, and only go in while a wave is on: a <strong>Tank</strong> (W, $900), an attack
      <strong>Helicopter</strong> (E, $1,200), a <strong>Predator</strong> drone (D, $1,400), and a
      <strong>Carrier strike</strong> (G, $2,500), a fighter off a carrier offshore that shoots down whatever flies.
      Shot down, a unit is gone; when the wave is over they all go home. Each has a cooldown after it is called.</p>
    <p>An <strong>A-10 strafing run</strong> (B, $2,200, during a wave) comes in low along the road through the tile
      you pick and rakes twelve tiles of it with its 30 mm cannon: armour takes it twice over.</p>
    <p>The <strong>AC-130</strong> (V, $4,500, during a wave) puts you in the gunner's seat for thirty seconds, in
      thermal: move the mouse to aim, click to fire, and 1, 2 and 3 (or the wheel) pick the 105 mm, the 40 mm and the
      25 mm, which fires as long as you hold it. A <strong>Nuclear strike</strong> (N, $12,000) wipes out everything for
      six tiles round its tile five seconds after the call, in the air too, but the fallout takes a fifth of the
      Command Center whatever, and up to half if it goes off close to it. It can end the game.</p>
    <h3>The fleet</h3>
    <p>From the sixth wave the enemy has ships out to sea, twenty tiles off the west coast, and nothing you build can
      reach them. They launch straight at the Command Center: drone carriers send Shaheds, frigates fire Kalibr cruise
      missiles, and each one that gets in costs integrity like anything off the road. The fight is in the air between
      the coast and the Command Center: Drone Interceptors and Air Defence for the Shaheds, an Iron Dome, a Patriot or
      a C-RAM for the Kalibrs, placed on the seaward side so they engage early. The ships sail out once their launchers
      are empty.</p>
    <h3>Saved games</h3>
    <p>The game is kept in this browser as you play; <strong>Continue</strong> on the menu picks it up where it was.
      Losing the Command Center ends it.</p>
    <h3>Camera</h3>
    <p>Drag to pan, wheel to zoom, the arrow keys to move. Hold Control and drag to turn the view; a Control-click
      puts it back.</p>
    <h3>The towers</h3>
    <ul class="units">${towerGuide()}</ul>
    <h3>The enemy</h3>
    <ul class="units">${enemyGuide()}</ul>
    <div class="row"><button class="credits link">Credits</button><button class="close primary">Close</button></div>
  </div>`;
  const close = () => wrap.remove();
  (wrap.querySelector('.close') as HTMLButtonElement).onclick = close;
  (wrap.querySelector('.credits') as HTMLButtonElement).onclick = () => openCredits();
  wrap.addEventListener('click', e => { if (e.target === wrap) close(); });
  wrap.addEventListener('keydown', e => { if (e.key === 'Escape') close(); e.stopPropagation(); });
  document.body.appendChild(wrap);
  (wrap.querySelector('.close') as HTMLButtonElement).focus();
}
